import { ReactNode } from 'react';
import { Card } from './Card';

interface Column<T> {
  key: string;
  label: string;
  render: (item: T) => ReactNode;
  mobileLabel?: string;
  hideOnMobile?: boolean;
}

interface ResponsiveTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (item: T) => string;
  emptyState?: ReactNode;
  isLoading?: boolean;
}

export function ResponsiveTable<T>({
  data,
  columns,
  keyExtractor,
  emptyState,
  isLoading
}: ResponsiveTableProps<T>) {
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="text-gray-500 mt-4 text-sm">Loading...</p>
      </div>
    );
  }

  if (data.length === 0) {
    return emptyState || (
      <div className="text-center py-12 text-gray-500">
        <p>No data available</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-3 font-semibold text-gray-700 text-xs uppercase tracking-wider"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {data.map((item) => (
              <tr key={keyExtractor(item)} className="hover:bg-gray-50 transition-colors">
                {columns.map((column) => (
                  <td key={column.key} className="px-6 py-4">
                    {column.render(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {data.map((item) => (
          <Card key={keyExtractor(item)} padding="md">
            <div className="space-y-3">
              {columns
                .filter((col) => !col.hideOnMobile)
                .map((column) => (
                  <div key={column.key} className="flex justify-between items-start gap-4">
                    <span className="text-sm font-medium text-gray-500 min-w-[100px]">
                      {column.mobileLabel || column.label}
                    </span>
                    <div className="text-sm text-gray-900 text-right flex-1">
                      {column.render(item)}
                    </div>
                  </div>
                ))}
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}
